import { action, computed, observable } from 'mobx'
import { StateAmountEntryViewModel } from '@/features/jobs/details/components/FinalReturnAmountsDialog/StateAmountEntryViewModel'

let nextId = 0

/**
 * A single tax year block: signed federal amount + per-state signed amounts.
 */
export class YearAmountEntryViewModel {
  @observable private accessor _federalAmountValue = 0

  readonly id = ++nextId
  @observable accessor taxYear = ''
  /**
   * Whether this entry actually has a federal amount, distinct from the
   * displayed `federalAmount` value (which defaults to 0 for display even
   * when absent). Starts `false` when constructed without a federal amount
   * (a genuinely new entry, or an existing year with none) and flips to
   * `true` whenever `federalAmount` is assigned via its setter — whether by
   * the Pro editing the field or by other code. This keeps a year that is
   * saved untouched from writing a bogus $0 federal amount.
   */
  @observable accessor hasFederalAmount = false
  @observable accessor stateEntries: StateAmountEntryViewModel[] = []

  constructor(
    taxYear = '',
    federalAmount?: number,
    stateEntries: StateAmountEntryViewModel[] = [],
  ) {
    this.taxYear = taxYear
    this._federalAmountValue = federalAmount ?? 0
    this.hasFederalAmount = federalAmount !== undefined
    this.stateEntries = stateEntries
  }

  /**
   * The signed federal amount, 0 when the year has none.
   */
  // Plain getter/setter, not `@computed` — the setter has a side effect
  // (flipping `hasFederalAmount`), which `@computed` accessors must not have.
  get federalAmount() {
    return this._federalAmountValue
  }

  set federalAmount(value: number) {
    this._federalAmountValue = value
    this.hasFederalAmount = true
  }

  /**
   * Whether any state entry still lacks a state.
   */
  @computed
  get hasIncompleteStates() {
    return this.stateEntries.some((e) => !e.isCompleted)
  }

  /**
   * Whether the year and all its state entries are filled in.
   */
  @computed
  get isCompleted() {
    return this.taxYear !== '' && !this.hasIncompleteStates
  }

  /**
   * Adds an empty state entry.
   */
  @action.bound
  addStateEntry() {
    this.stateEntries.push(new StateAmountEntryViewModel())
  }

  /**
   * Removes the state entry at `index`.
   */
  @action.bound
  removeStateEntry(index: number) {
    this.stateEntries.splice(index, 1)
  }
}
